import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TalentService } from '../talent.service';
import { TestAssignmentService } from '../test-assignment.service';
import { ComposeEmailComponent } from '../compose-email/compose-email.component';

@Component({
    selector: 'app-create-assignment-dialog',
    template: `
    <h2 mat-dialog-title>Create Test Assignment</h2>
    <mat-dialog-content>
      @if (errorMessage) {
        <div class="alert alert-danger">
          {{ errorMessage }}
        </div>
      }
    
      <!-- Assignment Mode Selector -->
      <mat-radio-group
        [(ngModel)]="assignmentMode"
        (change)="onModeChange()"
        style="display: flex; gap: 20px; margin-bottom: 20px;"
        >
        <mat-radio-button value="candidate">Assign to Candidate</mat-radio-button>
        <mat-radio-button value="email">Assign to Email Address</mat-radio-button>
      </mat-radio-group>
    
      <!-- Candidate Mode -->
      @if (assignmentMode === 'candidate') {
        <div>
          <mat-form-field appearance="outline" style="width: 100%; margin-top: 10px;">
            <mat-label>Search Candidate</mat-label>
            <input
              matInput
              [(ngModel)]="searchText"
              (ngModelChange)="searchCandidates()"
              placeholder="Type candidate name..."
              >
            </mat-form-field>
            @if (isSearching) {
              <div style="text-align: center; padding: 20px;">
                <mat-spinner diameter="40" style="margin: 0 auto;"></mat-spinner>
              </div>
            }
            @if (!isSearching && candidates.length > 0) {
              <mat-list style="max-height: 300px; overflow-y: auto;">
                @for (candidate of candidates; track candidate) {
                  <mat-list-item
                    (click)="selectCandidate(candidate)"
                    style="cursor: pointer; border-bottom: 1px solid #eee;"
                    >
                    <h4 matLine>{{ candidate.first_name }} {{ candidate.last_name }}</h4>
                    <p matLine>{{ candidate.email_address }}</p>
                  </mat-list-item>
                }
              </mat-list>
            }
            @if (!isSearching && searchText && candidates.length === 0) {
              <div style="text-align: center; padding: 20px; color: #999;">
                No candidates found
              </div>
            }
            @if (selectedCandidate) {
              <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
                <h4>Selected Candidate</h4>
                <p><strong>Name:</strong> {{ selectedCandidate.first_name }} {{ selectedCandidate.last_name }}</p>
                <p><strong>Email:</strong> {{ selectedCandidate.email_address }}</p>
              </div>
            }
          </div>
        }
    
        <!-- Direct Email Mode -->
        @if (assignmentMode === 'email') {
          <div>
            <mat-form-field appearance="outline" style="width: 100%; margin-bottom: 15px;">
              <mat-label>Email Address *</mat-label>
              <input
                matInput
                [(ngModel)]="directEmail"
                type="email"
                placeholder="candidate@example.com"
                required
                >
                <mat-hint>Enter the email address of the person taking the test</mat-hint>
              </mat-form-field>
              <mat-form-field appearance="outline" style="width: 100%;">
                <mat-label>Name (Optional)</mat-label>
                <input
                  matInput
                  [(ngModel)]="directName"
                  placeholder="John Doe"
                  >
                  <mat-hint>Optional: Enter name for easier identification</mat-hint>
                </mat-form-field>
                @if (directEmail) {
                  <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
                    <h4>Assignment Details</h4>
                    <p><strong>Email:</strong> {{ directEmail }}</p>
                    @if (directName) {
                      <p><strong>Name:</strong> {{ directName }}</p>
                    }
                    @if (!directName) {
                      <p><em>Name not provided</em></p>
                    }
                  </div>
                }
              </div>
            }
    
            @if (createdAssignment) {
              <div style="margin-top: 20px; padding: 15px; background-color: #d4edda; border-radius: 5px;">
                <h4 style="color: #155724;">Assignment Created!</h4>
                <p><strong>Test Link:</strong></p>
                <div style="display: flex; align-items: center; margin-bottom: 15px;">
                  <a
                    [href]="getTestLink()"
                    target="_blank"
                    style="flex: 1; padding: 10px; background-color: #fff; border: 1px solid #ccc; border-radius: 3px; text-decoration: none; color: #007bff; word-break: break-all;"
                    >
                    {{ getTestLink() }}
                  </a>
                  <button mat-icon-button (click)="copyLink()" matTooltip="Copy Link">
                    <mat-icon>content_copy</mat-icon>
                  </button>
                </div>
                <div style="display: flex; gap: 10px;">
                  <button mat-raised-button color="primary" (click)="sendEmail()" style="flex: 1;">
                    <mat-icon style="margin-right: 5px;">email</mat-icon>
                    Send Email
                  </button>
                </div>
                <p style="margin-top: 10px;"><small>Share this link with the candidate to access the test.</small></p>
              </div>
            }
          </mat-dialog-content>
          <mat-dialog-actions align="end">
            <button mat-button (click)="onCancel()">{{ createdAssignment ? 'Close' : 'Cancel' }}</button>
            <button
              mat-raised-button
              color="primary"
              (click)="onCreate()"
              [disabled]="!canCreate() || isCreating || createdAssignment"
              >
              {{ isCreating ? 'Creating...' : 'Create Assignment' }}
            </button>
          </mat-dialog-actions>
    `,
    styles: [`
    mat-list-item:hover {
      background-color: #f5f5f5;
    }
    code {
      font-family: monospace;
      font-size: 14px;
    }
  `],
    standalone: false
})
export class CreateAssignmentDialogComponent implements OnInit {
  assignmentMode: 'candidate' | 'email' = 'candidate';
  
  // Candidate mode
  searchText: string = '';
  candidates: any = [];
  selectedCandidate: any = null;
  
  // Email mode
  directEmail: string = '';
  directName: string = '';
  
  // Common
  createdAssignment: any = null;
  isSearching: boolean = false;
  isCreating: boolean = false;
  errorMessage: string = '';
  
  private searchTimeout: any;

  constructor(
    public dialogRef: MatDialogRef<CreateAssignmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private talentService: TalentService,
    private testAssignmentService: TestAssignmentService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {}

  onModeChange() {
    // Clear any selections/inputs when switching modes
    this.selectedCandidate = null;
    this.directEmail = '';
    this.directName = '';
    this.searchText = '';
    this.candidates = [];
    this.errorMessage = '';
  }

  canCreate(): boolean {
    if (this.assignmentMode === 'candidate') {
      return !!this.selectedCandidate;
    } else {
      // Email mode - must have valid email
      return !!this.directEmail && this.directEmail.includes('@');
    }
  }

  searchCandidates() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (!this.searchText || this.searchText.length < 2) {
      this.candidates = [];
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.isSearching = true;
      this.errorMessage = '';

      // Use the existing search endpoint (role_id 0 for general search)
      this.talentService.searchCandidates(0, this.searchText).subscribe(
        (results) => {
          this.candidates = results.results.slice(0, 20); // Limit to 20 results
          this.isSearching = false;
        },
        (error) => {
          console.error('Error searching candidates:', error);
          this.errorMessage = 'Failed to search candidates';
          this.isSearching = false;
        }
      );
    }, 300);
  }

  selectCandidate(candidate: any) {
    this.selectedCandidate = candidate;
    this.candidates = [];
    this.searchText = '';
  }

  onCreate() {
    if (!this.canCreate()) {
      if (this.assignmentMode === 'candidate') {
        this.errorMessage = 'Please select a candidate';
      } else {
        this.errorMessage = 'Please enter a valid email address';
      }
      return;
    }

    this.isCreating = true;
    this.errorMessage = '';

    // Call the service based on mode
    const observable = this.assignmentMode === 'candidate'
      ? this.talentService.createTestAssignment(
          this.data.testId,
          this.selectedCandidate.id
        )
      : this.talentService.createTestAssignment(
          this.data.testId,
          undefined,
          this.directEmail.trim(),
          this.directName.trim() || undefined
        );

    observable.subscribe(
      (response) => {
        this.isCreating = false;
        if (response.status === 'success') {
          this.createdAssignment = response.assignment;
        } else {
          this.errorMessage = response.message || 'Failed to create assignment';
        }
      },
      (error) => {
        this.isCreating = false;
        console.error('Error creating assignment:', error);
        this.errorMessage = 'Failed to create assignment';
      }
    );
  }

  getTestLink(): string {
    if (this.createdAssignment) {
      return this.testAssignmentService.getTestLink(this.createdAssignment.unique_code);
    }
    return '';
  }

  copyLink() {
    if (this.createdAssignment) {
      this.testAssignmentService.copyTestLink(this.createdAssignment.unique_code);
    }
  }

  sendEmail() {
    if (!this.createdAssignment) {
      return;
    }

    const testLink = this.getTestLink();
    const testName = this.data.testName || `Test #${this.data.testId}`;
    
    // Determine recipient info based on mode
    let recipientName = 'there';
    let recipientEmail = '';
    let candidateId = null;

    if (this.assignmentMode === 'candidate' && this.selectedCandidate) {
      recipientName = this.selectedCandidate.first_name;
      recipientEmail = this.selectedCandidate.email_address;
      candidateId = this.selectedCandidate.id;
    } else if (this.assignmentMode === 'email') {
      recipientName = this.directName || 'there';
      recipientEmail = this.directEmail;
    }

    if (!recipientEmail) {
      this.errorMessage = 'No recipient email available';
      return;
    }
    
    const emailBody = `
      <p>Hello ${recipientName},</p>
      
      <p>You have been assigned a technical assessment: <strong>${testName}</strong></p>
      
      <p>Please click the link below to access your test:</p>
      <p><a href="${testLink}">${testLink}</a></p>
      
      <p>This link is unique to you. Please complete the test at your earliest convenience.</p>
      
      <p><strong>Important:</strong> Please do not use AI or cut and paste code from public sites. All such activities are monitored and logged.</p>
      
      <p>If you have any questions, please don't hesitate to reach out.</p>
      
      <p>Best regards</p>
    `;

    this.dialog.open(ComposeEmailComponent, {
      height: '600px',
      width: '800px',
      data: {
        to: recipientEmail,
        subject: `SEQ Technology - Technical Assessment - ${testName}`,
        body: emailBody,
        candidate_id: candidateId,
        is_client_contact: false
      },
    });
  }

  onCancel() {
    this.dialogRef.close(this.createdAssignment ? true : false);
  }
}
